import { useCallback, useEffect, useState, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowsClockwise, CloudArrowDown, Copy, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL = "__ALL__";
const PAGE_SIZE = 50;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const STATE_STYLES = {
  APPROVED: "bg-emerald-100 text-emerald-700",
  PENDING: "bg-amber-100 text-amber-700",
  REVERSED: "bg-rose-100 text-rose-700",
};

export default function AdminFatCoupon() {
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [postbackInfo, setPostbackInfo] = useState(null);

  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, payout: 0, gross_sale_amount: 0 });
  const [loading, setLoading] = useState(false);

  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [syncingReport, setSyncingReport] = useState(false);
  const didFetch = useRef(false);

  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      // postback-info 404s until the inbound postback ships; that just hides the card.
      const [{ data: h }, pb] = await Promise.all([
        API.get("/admin/fatcoupon/health"),
        API.get("/admin/fatcoupon/postback-info").catch(() => null),
      ]);
      setHealth(h);
      setPostbackInfo(pb?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load FatCoupon health");
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        network: "fatcoupon",
        // Network page = reconciliation view: list $0 conversions too.
        include_zero: "1",
      });
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (stateFilter) params.set("state", stateFilter);
      const { data } = await API.get(`/admin/actions?${params.toString()}`);
      let items = data.items || [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        items = items.filter((r) =>
          [r.action_id, r.campaign_name, r.advertiser_name, r.sub_id1, r.rid]
            .some((v) => String(v || "").toLowerCase().includes(q)),
        );
      }
      setRows(items);
      setTotal(data.total || 0);
      setStats(data.stats || { total: 0, payout: 0, gross_sale_amount: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load FatCoupon conversions");
    } finally {
      setLoading(false);
    }
  }, [page, startDate, endDate, stateFilter, search]);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchHealth();
  }, [fetchHealth]);
  useEffect(() => {
    const t = setTimeout(fetchRows, 250);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const handleSyncCatalog = async () => {
    setSyncingCatalog(true);
    try {
      const { data } = await API.post("/admin/fatcoupon/sync-catalog");
      toast.success(
        `Catalog sync ${data.status || "ok"} · seen ${data.counts?.brands_seen ?? 0} · upserted ${data.counts?.upserted ?? 0}`,
      );
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Catalog sync failed");
    } finally {
      setSyncingCatalog(false);
    }
  };

  const handleSyncReport = async () => {
    setSyncingReport(true);
    try {
      // FatCoupon's sync-report reads its window from the query string, not a body.
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const { data } = await API.post(`/admin/fatcoupon/sync-report?${params.toString()}`);
      const c = data.counts || {};
      toast.success(
        `Report sync ${data.status || "ok"} · seen ${c.rows_seen ?? 0} · ingested ${c.ingested ?? 0}`,
      );
      fetchHealth();
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Report sync failed");
    } finally {
      setSyncingReport(false);
    }
  };

  const copyPostback = async () => {
    const url = postbackInfo?.url_template || "";
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Postback URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-fatcoupon-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-teal-700">Coupon extension</p>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">FatCoupon</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Store catalog sync and hourly order pull for FatCoupon.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleSyncCatalog} disabled={syncingCatalog} className="bg-teal-700 hover:bg-teal-800 text-white">
            <CloudArrowDown size={14} className="mr-2" />
            {syncingCatalog ? "Syncing catalog…" : "Sync catalog"}
          </Button>
          <Button onClick={handleSyncReport} disabled={syncingReport} variant="outline">
            <ArrowsClockwise size={14} className="mr-2" />
            {syncingReport ? "Syncing report…" : "Sync report"}
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md p-4 mb-6 bg-white grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Configured</div>
          <div className="font-mono mt-1">{loadingHealth ? "…" : (health?.configured ? "yes" : "no — set FATCOUPON_API_USER / FATCOUPON_API_PASSWORD")}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Brands (active)</div>
          <div className="font-mono mt-1">{health ? `${health.brand_count} (${health.active_brand_count})` : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Actions</div>
          <div className="font-mono mt-1">{health?.action_count ?? "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">API base</div>
          <div className="font-mono mt-1 text-xs truncate">{health?.api_base || "—"}</div>
        </div>
      </div>

      {postbackInfo && (
        <div className="border border-teal-200 rounded-md p-4 mb-6 bg-teal-50/40">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-teal-900">
              <Lightning size={16} /> Inbound postback URL (FatCoupon → Postback Settings)
            </div>
            <Button size="sm" variant="outline" onClick={copyPostback}>
              <Copy size={14} className="mr-1" /> Copy
            </Button>
          </div>
          <code className="block text-[11px] font-mono break-all text-slate-700 bg-white border border-teal-100 rounded p-2">
            {postbackInfo.url_template || "—"}
          </code>
          <p className="text-xs text-slate-500 mt-2">
            {postbackInfo.secret_configured ? "Secret key set." : "Secret key not set — the endpoint refuses until FATCOUPON_POSTBACK_SECRET is configured."}
          </p>
        </div>
      )}

      <div className="border border-slate-200 rounded-md p-4 mb-4 bg-white flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Start</Label>
          <Input type="date" value={startDate} onChange={(e) => { setPage(1); setStartDate(e.target.value); }} className="h-9 w-40" />
        </div>
        <div>
          <Label className="text-xs">End</Label>
          <Input type="date" value={endDate} onChange={(e) => { setPage(1); setEndDate(e.target.value); }} className="h-9 w-40" />
        </div>
        <div>
          <Label className="text-xs">State</Label>
          <Select value={stateFilter || ALL} onValueChange={(v) => { setPage(1); setStateFilter(v === ALL ? "" : v); }}>
            <SelectTrigger className="h-9 w-40"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All</SelectItem>
              <SelectItem value="APPROVED">APPROVED</SelectItem>
              <SelectItem value="PENDING">PENDING</SelectItem>
              <SelectItem value="REVERSED">REVERSED</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs">Search</Label>
          <Input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="action / brand / sub" className="h-9" />
        </div>
        <div className="text-xs text-slate-500 pb-2">
          {stats.total} conv · ${Number(stats.payout || 0).toFixed(2)} payout
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Brand</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Sub / RID</th>
              <th className="px-3 py-2">Sale</th>
              <th className="px-3 py-2">Payout</th>
              <th className="px-3 py-2">State</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No conversions</td></tr>
            ) : rows.map((r) => (
              <tr key={r.action_id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{(r.event_date || "").slice(0, 19)}</td>
                <td className="px-3 py-2">{r.campaign_name || r.advertiser_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.action_id}</td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  <div>{r.sub_id1 || "—"}</div>
                  <div className="text-slate-400">{r.rid || r.sub_id2 || ""}</div>
                </td>
                <td className="px-3 py-2">${Number(r.amount || 0).toFixed(2)}</td>
                <td className="px-3 py-2">${Number(r.payout || 0).toFixed(2)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATE_STYLES[r.state] || "bg-slate-100 text-slate-600"}`}>
                    {r.state || "—"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
        <span>{total} total</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <span className="px-2 py-1">Page {page}</span>
          <Button size="sm" variant="outline" disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
